import deTranslations from "../errors/de.json"
import enTranslations from "../errors/en.json"
import esTranslations from "../errors/es.json"
import frTranslations from "../errors/fr.json"
import jaTranslations from "../errors/ja.json"
import nlTranslations from "../errors/nl.json"
import { ErrorCode, ErrorTranslationParams, ErrorTranslations, ErrorType } from "../errors/types"

/**
 * Available locales for error translations
 * Matches frontend i18n locale support: EN, FR, DE, ES, JA, NL
 */
export type ErrorLocale = "en" | "fr" | "de" | "es" | "ja" | "nl"

/**
 * Map of locale to translation files
 * Typed properly to allow string indexing with ErrorCode
 */
const translations: Record<ErrorLocale, ErrorTranslations> = {
  en: enTranslations as ErrorTranslations,
  fr: frTranslations as ErrorTranslations,
  de: deTranslations as ErrorTranslations,
  es: esTranslations as ErrorTranslations,
  ja: jaTranslations as ErrorTranslations,
  nl: nlTranslations as ErrorTranslations
}

/**
 * Interpolates parameters into a translated message
 *
 * @example
 * interpolateParams("Value must be at least {min} characters", { min: 3 })
 * // Returns: "Value must be at least 3 characters"
 */
function interpolateParams(message: string, params?: ErrorTranslationParams): string {
  if (!params) return message

  return Object.entries(params).reduce((msg, [key, value]) => {
    return msg.replace(new RegExp(`\\{${key}\\}`, "g"), String(value))
  }, message)
}

/**
 * Gets a translated error message for the given error type and code
 *
 * Features:
 * - Automatic locale detection (defaults to 'en')
 * - Parameter interpolation (e.g., {min}, {max}, {retryAfter})
 * - Fallback to backend message if translation not found
 * - Fallback to English if locale translation missing
 * - Type-safe error type and code usage
 *
 * @param errorType - The error type category (e.g., 'validation_error')
 * @param errorCode - The specific error code (e.g., 'invalid_email')
 * @param params - Optional parameters for interpolation (e.g., { min: 3, max: 100 })
 * @param fallbackMessage - Fallback message from backend if translation not found
 * @param locale - The locale to use (defaults to 'en')
 *
 * @returns Translated and interpolated error message
 *
 * @example
 * // Simple usage
 * getErrorMessage('validation_error', 'invalid_email')
 * // Returns: "The email address format is invalid"
 *
 * @example
 * // With parameters
 * getErrorMessage('validation_error', 'value_too_short', { min: 3 })
 * // Returns: "This value must be at least 3 characters long"
 *
 * @example
 * // With fallback
 * getErrorMessage('validation_error', 'custom_error', undefined, 'Something went wrong')
 * // Returns: "Something went wrong"
 *
 * @example
 * // With French locale
 * getErrorMessage('validation_error', 'invalid_email', undefined, undefined, 'fr')
 * // Returns: "Le format de l'adresse e-mail est invalide"
 */
export function getErrorMessage(
  errorType: ErrorType | string,
  errorCode: ErrorCode | string,
  params?: ErrorTranslationParams,
  fallbackMessage?: string,
  locale: ErrorLocale = "en"
): string {
  // Try to get translation for requested locale
  const localeTranslations = translations[locale]
  const typeTranslations = localeTranslations?.errors?.[errorType as ErrorType]
  let message = typeTranslations?.[errorCode]

  // Fallback to English if translation not found in requested locale
  if (!message && locale !== "en") {
    const enTypeTranslations = translations.en.errors[errorType as ErrorType]
    message = enTypeTranslations?.[errorCode]
  }

  // Fallback to backend message if no translation found
  if (!message && fallbackMessage) {
    message = fallbackMessage
  }

  // Final fallback to generic message
  if (!message) {
    message = "An error occurred. Please try again."
  }

  // Interpolate parameters
  return interpolateParams(message, params)
}

/**
 * Generates an i18n key from error type and code
 * Useful for direct integration with i18n libraries like i18next
 *
 * @param errorType - The error type category
 * @param errorCode - The specific error code
 * @returns i18n key in format "errors.{type}.{code}"
 *
 * @example
 * generateErrorKey('validation_error', 'invalid_email')
 * // Returns: "errors.validation_error.invalid_email"
 */
export function generateErrorKey(
  errorType: ErrorType | string,
  errorCode: ErrorCode | string
): string {
  return `errors.${errorType}.${errorCode}`
}

/**
 * Type guard to check if a string is a valid ErrorType
 */
export function isErrorType(value: string): value is ErrorType {
  return Object.values(ErrorType).includes(value as ErrorType)
}

/**
 * Type guard to check if a string is a valid ErrorCode
 */
export function isErrorCode(value: string): value is ErrorCode {
  return Object.values(ErrorCode).includes(value as ErrorCode)
}
